import { useCallback, useMemo } from 'react';
import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import type { Action, State } from '../shared/useListReducer';
import type { TraineeReportRowDataFragment } from './TraineeReports.api';
import { useFetchTraineeReportsQuery } from './TraineeReports.api';
import type { ListRow } from './useColumns';
import useColumns from './useColumns';
import { TraineeReportSortingField, SortingOrder as GqlSortingOrder, TrainersNotesStatus } from '@/apolloClient.types';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Loader from '@/components/common/Loader';
import Table from '@/components/common/Table';
import {
    SortingOrder as TableSortingOrder,
    type SortOrder as TableSortOrderValue,
} from '@/components/common/Table/types';
import TableFooter from '@/components/common/TableFooter';
import { useGetCompanyId } from '@/contexts/UserSession';

export interface TraineeListProps {
    state: State;
    dispatch: Dispatch<Action>;
    handleView: (record: ListRow) => void;
    handleDownload: (record: ListRow) => void;
    handleEdit?: (record: ListRow) => void;
}

const pageSizeOptions = [10, 25, 50, 100];

const TraineeList = ({ state, dispatch, handleView, handleDownload, handleEdit }: TraineeListProps) => {
    const { t } = useTranslation('traineeReports');
    const selectedCompanyId = useGetCompanyId();
    const { page, pageSize } = state;
    const sort = state.sort;

    const { loading, data, error } = useFetchTraineeReportsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: selectedCompanyId ?? '',
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
            filter: state.filter,
            sort: { field: sort.field, order: sort.order },
        },
        skip: !selectedCompanyId,
    });

    const columns = useColumns(handleView, handleDownload, handleEdit, sort);

    const getSortField = useCallback((columnKey?: string): TraineeReportSortingField | undefined => {
        switch (columnKey) {
            case 'programmeName':
                return TraineeReportSortingField.Programme;
            case 'assessmentRunName':
                return TraineeReportSortingField.AssessmentRunName;
            case 'traineeName':
                return TraineeReportSortingField.TraineeName;
            case 'startDate':
                return TraineeReportSortingField.StartDate;
            case 'endDate':
                return TraineeReportSortingField.EndDate;
            case 'completionDate':
                return TraineeReportSortingField.CompletionDate;
            case 'groupName':
                return TraineeReportSortingField.GroupName;
            case 'subGroupName':
                return TraineeReportSortingField.SubGroupName;
            case 'trainersNotesStatus':
                return TraineeReportSortingField.TrainersNotesStatus;
            default:
                return undefined;
        }
    }, []);

    const handleSortChange = useCallback(
        (columnKey?: string, order?: TableSortOrderValue) => {
            const field = getSortField(columnKey);

            if (!field || !order) {
                // Reset to default if sorting cleared or unsupported column
                dispatch({
                    type: 'SET_SORT',
                    sortBy: {
                        field: TraineeReportSortingField.Programme,
                        order: GqlSortingOrder.Asc,
                        orderValue: TableSortingOrder.Asc,
                    },
                });
                return;
            }

            dispatch({
                type: 'SET_SORT',
                sortBy: {
                    field,
                    order: order === TableSortingOrder.Asc ? GqlSortingOrder.Asc : GqlSortingOrder.Desc,
                    orderValue: order,
                },
            });
        },
        [dispatch, getSortField]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            dispatch({ type: 'SET_PAGE', page: newPage });
        },
        [dispatch]
    );

    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            dispatch({ type: 'SET_PAGE_SIZE', pageSize: newPageSize });
        },
        [dispatch]
    );
    const dataSource: ListRow[] = useMemo(() => {
        const items: TraineeReportRowDataFragment[] = data?.traineeReports?.items || [];
        return items.map(item => ({
            id: `${item.assessmentRunId}-${item.userId}`,
            userId: item.userId,
            traineeName: item.traineeName || t('emptyValue'),
            programmeName: item.programName || t('emptyValue'),
            assessmentRunName: item.assessmentRunName || t('emptyValue'),
            groupName: item.groupName || t('emptyValue'),
            subGroupName: (item as any).subGroupName || t('emptyValue'),
            assessmentRunId: item.assessmentRunId,
            programId: item.programId || undefined,
            startDate: item.startDate ? new Date(item.startDate) : undefined,
            endDate: item.endDate ? new Date(item.endDate) : undefined,
            completionDate: item.completionDate ? new Date(item.completionDate) : undefined,
            trainersNotesStatus: item.trainersNotesStatus || TrainersNotesStatus.None,
            canEdit: !!item.canEdit,
        }));
    }, [data?.traineeReports?.items, t]);

    const totalCount = useMemo(
        () => data?.traineeReports?.count || dataSource?.length || 0,
        [data?.traineeReports?.count, dataSource]
    );

    if (error) {
        return (
            <EmptyContainer>
                <Empty title={t('empty.title')} />
            </EmptyContainer>
        );
    }

    if (loading && !dataSource?.length) {
        return <Loader />;
    }

    if (!dataSource?.length && !loading) {
        return (
            <EmptyContainer>
                <Empty title={t('error.noTraineeList')} />
            </EmptyContainer>
        );
    }

    return (
        <div>
            <Table<ListRow>
                columns={columns}
                dataSource={dataSource}
                rowKey="id"
                onSortChange={handleSortChange}
                headCellClassName={() => {
                    return '!text-neutral-900 !font-semibold';
                }}
            />

            {totalCount > 0 && (
                <div className="mt-4">
                    <TableFooter
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        pageSizeOptions={pageSizeOptions}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default TraineeList;
